import { useEffect, useState } from "react";
import { Download, X, RefreshCw } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { registerServiceWorker, triggerSWUpdate } from "@/lib/pwa";

const DISMISS_KEY = "mazra3ti:pwa-install-dismissed";
const SNOOZE_DAYS = 1; // shorter than before so users see the prompt more often

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function PWAManager() {
  const { t } = useLang();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);
  const [waitingReg, setWaitingReg] = useState(null);

  useEffect(() => {
    registerServiceWorker({
      onUpdateAvailable: (registration) => setWaitingReg(registration),
    });

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      const dismissed = window.localStorage.getItem(DISMISS_KEY);
      // Snooze for SNOOZE_DAYS days after dismissal
      if (dismissed && Date.now() - Number(dismissed) < SNOOZE_DAYS * 24 * 3600 * 1000) {
        return;
      }
      if (!isStandalone()) setShowInstall(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setShowInstall(false);
      setShowIosHint(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS doesn't fire beforeinstallprompt; show a one-time manual hint instead.
    if (isIos() && !isStandalone()) {
      const dismissed = window.localStorage.getItem(DISMISS_KEY);
      if (!dismissed) {
        const timer = setTimeout(() => setShowIosHint(true), 4000);
        return () => {
          clearTimeout(timer);
          window.removeEventListener("beforeinstallprompt", onBeforeInstall);
          window.removeEventListener("appinstalled", onInstalled);
        };
      }
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null);
    setShowInstall(false);
  };

  const handleDismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowInstall(false);
    setShowIosHint(false);
  };

  const handleUpdate = () => {
    triggerSWUpdate(waitingReg);
    setWaitingReg(null);
  };

  const showAnyInstall = showInstall || showIosHint;

  return (
    <>
      {showAnyInstall && (
        <div
          data-testid="pwa-install-banner"
          className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-md rounded-2xl border border-[#E2E4DF] bg-white p-4 shadow-2xl sm:bottom-6"
          role="dialog"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1C3A27] text-[#FAFAF7]">
              <Download className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#1C3A27]">
                {t.pwa?.install_title || "Installer Mazra3ti"}
              </p>
              <p className="mt-1 text-xs leading-snug text-[#5C6B5C]">
                {showIosHint
                  ? t.pwa?.ios_hint
                  : t.pwa?.install_desc}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {showInstall && (
                  <button
                    data-testid="pwa-install-btn"
                    onClick={handleInstall}
                    className="rounded-lg bg-[#1C3A27] px-3 py-1.5 text-xs font-semibold text-[#FAFAF7] transition-opacity hover:opacity-90"
                  >
                    {t.pwa?.install_cta || "Installer"}
                  </button>
                )}
                <button
                  data-testid="pwa-install-dismiss-btn"
                  onClick={handleDismiss}
                  className="rounded-lg border border-[#E2E4DF] bg-white px-3 py-1.5 text-xs font-medium text-[#5C6B5C] transition-colors hover:bg-[#F0F0EB]"
                >
                  {t.pwa?.install_dismiss || "Plus tard"}
                </button>
              </div>
            </div>
            <button
              aria-label="Close"
              onClick={handleDismiss}
              className="text-[#5C6B5C] hover:text-[#1C3A27]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {waitingReg && (
        <div
          data-testid="pwa-update-banner"
          className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 rounded-full border border-[#1C3A27] bg-[#1C3A27] px-4 py-2 text-xs text-[#FAFAF7] shadow-xl"
          role="status"
        >
          <div className="flex items-center gap-3">
            <RefreshCw className="h-4 w-4" />
            <span className="font-medium">
              {t.pwa?.update_title || "Nouvelle version disponible"}
            </span>
            <button
              data-testid="pwa-update-btn"
              onClick={handleUpdate}
              className="rounded-full bg-[#E05A3D] px-3 py-1 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            >
              {t.pwa?.update_cta || "Mettre à jour"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
